import { canEditIdeaTest, isGuideAnIdeaTest } from '@/voc/helpers/common.helper';
import { $can } from '@pendo/permissions';
import { isCrossApp } from '@pendo/services/CrossAppGuides';
import { isNpsGuide } from '@/vuex/modules/guides/guides.utils';
import get from 'lodash/get';

export function canCreateGuide ({ guide, source, target = 'ANY' }) {
    if (canEditIdeaGuide(guide)) return true;

    return hasPermissionGuideAction({ guide, source, target, action: 'create' });
}

export function canEditIdeaGuide (guide) {
    return isGuideAnIdeaTest(guide) && canEditIdeaTest();
}

export function canEditGuide ({ appIds, guide, field, source, target = 'ANY' }) {
    if (!guide) return false;
    if (canEditIdeaGuide(guide)) return true;

    const sourceWithDefaultValue = source || guide.state;

    return hasPermissionGuideAction({ appIds, guide, field, target, action: 'edit', source: sourceWithDefaultValue });
}

export function canDeleteGuide ({ guide, source, target = 'ANY' }) {
    if (canEditIdeaGuide(guide)) return true;

    return hasPermissionGuideAction({ guide, target, source, action: 'remove' });
}

export function canPublishGuide ({ appIds, guide, target = '*', source = '*' }) {
    return hasPermissionGuideAction({ appIds, guide, target, action: 'publish', source });
}

export function hasPermissionGuideAction ({ appIds, guide, field, target = 'ANY', action = 'edit', source = '*' }) {
    const appIdsToCheck = appIds || allAppIdsforGuide(guide);
    const entityType = isNpsGuide(guide) ? 'nps' : 'guide';
    const invalidApps = appIdsToCheck.filter((appId) => {
        return !$can(appId)
            .entity(entityType, { entity: guide, field })
            .action(action)
            .source(source)
            .target(target);
    });

    return invalidApps.length === 0;
}

export function allAppIdsforGuide (guide) {
    if (guide.appIds) return guide.appIds;

    if (!isCrossApp(guide)) {
        const appId = guide.appId || get(guide, 'app.id');

        return [appId];
    }

    const appIds = {};
    guide.steps.forEach((step) => {
        const appId = step.appIds[0];
        appIds[appId] = appId;
    });

    return Object.values(appIds);
}

export function canCreateAnyGuideFromScratch (appIds) {
    return appIds.some((id) => {
        return $can(id)
            .entity('guide')
            .action('create')
            .source('scratch')
            .target('ANY');
    });
}

export function canEditAnyGuide (appIds) {
    return appIds.some((id) => {
        return $can(id)
            .entity('guide')
            .action('edit')
            .source('ANY')
            .target('ANY');
    });
}

export function canCreateGuideByAppId ({ appId }) {
    if (!appId) return false;

    return $can(appId)
        .entity('guide')
        .action('create')
        .source('ANY')
        .target('ANY');
}

export function canEditGuideByAppId ({ appId }) {
    if (!appId) return false;

    return $can(appId)
        .entity('guide')
        .action('edit')
        .source('ANY')
        .target('ANY');
}

export function canArchiveGuide ({ guide, source, target = 'ANY' }) {
    return canEditGuide({ guide, source, target }) || canDeleteGuide({ guide, source, target });
}

export function canRestoreGuide ({ guide, source, target = 'ANY' }) {
    return canArchiveGuide({ guide, source, target });
}
